import { CSSProperties, FC, useState } from "react";
import { Parser } from "@json2csv/plainjs";
import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";

import { Button } from "components";
import { Track } from "services/valuation/types";
import theme from "theme";

const handleDownloadTracklistCsv = (tracks: Track[], listingId: number) => {
  const fields = [
    { label: "Track Id", value: "id" },
    { label: "Title", value: "title" },
    { label: "Parent Id", value: "parent_id" },
  ];
  const parser = new Parser({ fields });
  const csv = parser.parse(tracks);

  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.download = !!listingId
    ? `Listing #${listingId} Included Track List.csv`
    : "Included Track List.csv";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const tableHeaderCellCommonStyles: CSSProperties = {
  fontSize: "1rem",
  fontWeight: "normal",
  backgroundColor: theme.custom.palette.gray.main,
  borderBottom: "none",
  border: `2px solid ${theme.custom.palette.gray.main}`,
};

const useStyles = makeStyles({
  tableContainer: {
    display: "flex",
    justifyContent: "center",
  },
  table: {
    borderCollapse: "separate",
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[3],
    width: "74%",
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
    "& th:first-child": {
      borderTopLeftRadius: theme.shape.borderRadius,
    },
    "& th:last-child": {
      borderTopRightRadius: theme.shape.borderRadius,
    },
  },
  tableHeaderLeftCell: {
    ...tableHeaderCellCommonStyles,
    borderRight: "unset",
    textAlign: "left",
    paddingLeft: theme.spacing(6),
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(3),
    },
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(3),
    },
  },
  tableHeaderMidCell: {
    ...tableHeaderCellCommonStyles,
    textAlign: "center",
  },
  tableHeaderRightCell: {
    ...tableHeaderCellCommonStyles,
    textAlign: "center",
  },
  tableBody: {
    borderRadius: theme.shape.borderRadius,
    "& tr > td": {
      border: `2px solid ${theme.custom.palette.gray.main}`,
      borderBottom: "unset",
      "&:first-child": {
        borderRight: "unset",
      },
    },
    "& tr:last-of-type": {
      "& td": {
        borderBottom: `2px solid ${theme.custom.palette.gray.main}`,
      },
      "& td:first-of-type": {
        borderBottomLeftRadius: theme.shape.borderRadius,
      },
      "& td:last-of-type": {
        borderBottomRightRadius: theme.shape.borderRadius,
      },
    },
  },
  tableCellLeft: {
    textAlign: "left",
    paddingLeft: theme.spacing(6),
    [theme.breakpoints.down("sm")]: {
      paddingLeft: theme.spacing(3),
    },
    [theme.breakpoints.down("xs")]: {
      paddingLeft: theme.spacing(2),
      maxWidth: "130px !important",
    },
  },
  tableCellMid: {
    textAlign: "center",
    width: "25% !important",
  },
  tableCellRight: {
    textAlign: "center",
    borderLeft: "unset !important",
    width: "25% !important",
  },
  showMoreContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2),
  },
  showMoreButton: {
    color: theme.custom.palette.blue.main,
    textDecoration: "underline",
  },
  hiddenTrackListText: {
    textAlign: "left",
    [theme.breakpoints.down("sm")]: {
      textAlign: "center",
    },
  },
});

interface TrackListProps {
  listingId?: number;
  tracks: Track[];
  isTrackListHidden: boolean;
}

const Tracks: FC<TrackListProps> = ({
  listingId,
  tracks,
  isTrackListHidden,
}) => {
  const classes = useStyles();
  const [showMore, setShowMore] = useState(false);
  const displayRows = showMore ? tracks.length : 10;

  const canDisplayTracksTable = tracks.length <= 100;

  const parentTrackMap = new Map<string, string[]>();

  const filterAndLinkParentTracks = (tracks: Track[]) => {
    return tracks.flatMap(track => {
      if (track.parent_id) {
        if (parentTrackMap.has(track.parent_id)) {
          parentTrackMap.get(track.parent_id).push(track.id);
        } else {
          parentTrackMap.set(track.parent_id, [track.id]);
        }
        return [];
      }
      return [track];
    });
  };

  const sortedTracks = filterAndLinkParentTracks(
    tracks.sort((a, b) => {
      // Sort "0" last
      if (a.id === "0") {
        return 1;
      }
      if (b.id === "0") {
        return -1;
      }
      // Then sort "ADJ"
      if (a.id === "ADJ" && b.id !== "0") {
        return 1;
      }
      if (b.id === "ADJ" && a.id !== "0") {
        return -1;
      }
      // Finally, sort by title alphabetically
      return a.title.localeCompare(b.title);
    }),
  );

  return (
    <Box>
      {isTrackListHidden ? (
        <Box mx={0.5} my={2}>
          <Typography>
            Please refer to the Other Information section to download full track
            list.
          </Typography>
        </Box>
      ) : (
        <>
          {canDisplayTracksTable && (
            <>
              <TableContainer className={classes.tableContainer}>
                <Table className={classes.table}>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderLeftCell}>
                        Track Title
                      </TableCell>
                      <TableCell className={classes.tableHeaderMidCell}>
                        Track ID
                      </TableCell>
                      {parentTrackMap.size !== 0 && (
                        <TableCell className={classes.tableHeaderRightCell}>
                          Alternate ID
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody className={classes.tableBody}>
                    {sortedTracks.slice(0, displayRows).map((data, index) => (
                      <TableRow key={index}>
                        <TableCell className={classes.tableCellLeft}>
                          <Typography>{data.title}</Typography>
                        </TableCell>
                        <TableCell className={classes.tableCellMid}>
                          {data.id}
                        </TableCell>
                        {parentTrackMap.size !== 0 && (
                          <TableCell className={classes.tableCellRight}>
                            {parentTrackMap.has(data.id)
                              ? parentTrackMap.get(data.id).join(", ")
                              : ""}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box className={classes.showMoreContainer}>
                {tracks.length > 10 && (
                  <Button onClick={() => setShowMore(!showMore)}>
                    {showMore
                      ? "Show less Tracks"
                      : `Click to see all Tracks (${tracks.length})`}
                  </Button>
                )}
              </Box>
            </>
          )}
          <Box mt={2} mb={16} textAlign="center">
            <Button
              variant="contained"
              size="large"
              onClick={() => handleDownloadTracklistCsv(tracks, listingId)}
            >
              Download Included Track List
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Tracks;
